import axios from "axios";

import { BuildConfig } from "@uic/adapters/confenv";

export { WscErrorKind, classifyWscError } from "./errors";

export const wsc = axios.create({
  baseURL: `${BuildConfig.WSC_ADDRESS.replace(/\/+$/, "")}/wscapi`,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

let privateTokenFn = null;
let privateOrgMemberIdFn = null;

/*
 * Add a nice authorization token to requests
 * when the user is logged in, and add org member ID header
 */
wsc.interceptors.request.use((config) => {
  const token = privateTokenFn?.();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add org member ID header (skip for /organizations endpoint)
  const orgMemberId = privateOrgMemberIdFn?.();
  if (orgMemberId && !config.url?.includes("/organizations")) {
    config.headers["X-Organization-Member-Id"] = orgMemberId;
  }

  return config;
});

/*
 * Ensure we don't retry a bunch of requests if the
 * user is logged out
 */
wsc.interceptors.response.use(
  (response) => response,
  (error) => {
    // Signup is a public endpoint called without a token, so a 403 there is a
    // real rejection (e.g. an email that is not on the allowlist) rather than
    // an artifact of being logged out, and must reach the caller unchanged.
    const isPublicEndpoint = error.config?.url === "/signup";
    if (
      !isPublicEndpoint &&
      (error.response?.status === 401 || error.response?.status === 403)
    ) {
      const token = privateTokenFn?.();
      if (!token) {
        return Promise.reject(
          new axios.Cancel("Request cancelled: user logged out"),
        );
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Will set the provided function to access a token and
 * apply it as an interceptor for the axios client.
 * @param {() => string} tokenFn The function to access the token
 */
export function setTokenFn(tokenFn) {
  privateTokenFn = tokenFn;
}

/**
 * Will set the provided function to access the organization member ID
 * and apply it as an interceptor for the axios client.
 * @param {() => string|null} orgMemberIdFn The function to access the org member ID
 */
export function setOrgMemberIdFn(orgMemberIdFn) {
  privateOrgMemberIdFn = orgMemberIdFn;
}
